import { jsonSchema } from "@redotech/redo-automations-model/graph/automation";
import { automationTestSchema } from "@redotech/redo-automations-runtime/testing/automation-tests";
import * as z from "zod";
import {
  definitionInputSchema,
  diagnosticSchema,
  serializedLength,
  stepRecordSchema,
} from "./common";

/** A test block's scenario: the event plus canned answers for lookups, effects, events and time. */
export const scenarioSchema = automationTestSchema.shape.scenario;
export type AutomationScenario = z.infer<typeof scenarioSchema>;

/** Simulation runs on the request thread, so a scenario is capped like a definition. */
export const SCENARIO_LIMITS = { entries: 500, characters: 1_000_000 } as const;

const scenarioInputSchema = scenarioSchema.superRefine((scenario, ctx) => {
  if (serializedLength(scenario) > SCENARIO_LIMITS.characters) {
    ctx.addIssue({
      code: "custom",
      message: `A scenario can be at most ${SCENARIO_LIMITS.characters} characters`,
    });
  }
  const counts = {
    records: Object.keys(scenario.records ?? {}).length,
    resolvers: Object.keys(scenario.resolvers ?? {}).length,
    events: scenario.events?.length ?? 0,
    timeline: scenario.timeline?.length ?? 0,
    refreshes: scenario.refreshes?.length ?? 0,
  };
  for (const [key, count] of Object.entries(counts)) {
    if (count > SCENARIO_LIMITS.entries) {
      ctx.addIssue({
        code: "custom",
        path: [key],
        message: `A scenario can have at most ${SCENARIO_LIMITS.entries} ${key}`,
      });
    }
  }
});

/**
 * Runs a definition against a scenario on the simulation host ("Test
 * automation"): nothing real is read or performed, except that Function steps
 * run their saved code in RedoJS.
 */
export const simulate = {
  name: "simulate" as const,
  input: z.object({
    definition: definitionInputSchema,
    scenario: scenarioInputSchema,
  }),
  output: z.discriminatedUnion("ok", [
    z.object({
      ok: z.literal(true),
      status: z.enum([
        "running",
        "completed",
        "failed",
        "rejected",
        "cancelled",
      ]),
      /** Node ids in execution order, including skipped ones. */
      path: z.array(z.string()),
      steps: z.array(stepRecordSchema),
      /** The effect the run is parked on when the scenario never answers it (e.g. an event that never arrives). */
      suspendedOn: z.string().nullable(),
      records: z.record(z.string(), z.record(z.string(), jsonSchema)),
      /** Lookups nothing answered; the run used each field's fallback. */
      unanswered: z.array(
        z.object({ resolver: z.string(), field: z.string() }),
      ),
      failure: z
        .object({ nodeId: z.string().nullable(), message: z.string() })
        .nullable(),
      rejection: z.string().nullable(),
      /** What each Function step's code logged, by step id. */
      logs: z.record(z.string(), z.string()),
    }),
    z.object({ ok: z.literal(false), diagnostics: z.array(diagnosticSchema) }),
  ]),
};
